import { AxiosError, AxiosResponse } from 'axios';
import { action, actionBound, computed, observable } from 'mobx';
import { UiResponse } from '../CommonInterfaces';
import { API } from '../util/ApiUtil';
import ErrorUtil from '../util/ErrorUtil';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import LanguageStore from './LanguageStore';

export interface EmailRegisterCredentials {
  email: string;
  firstName: string;
  lastName: string;
}

export interface FormErrors {
  emailError?: string;
  firstNameError?: string;
  lastNameError?: string;
  generalError?: string;
}

export const formErrors: (keyof FormErrors)[] = ['emailError', 'firstNameError', 'lastNameError', 'generalError'];
export const FORM_ERROR_CODE = 'form.error';

export default class EmailRegisterStore {
  @observable accessor emailRegisterCredentials: EmailRegisterCredentials = { email: '', firstName: '', lastName: '' };
  @observable accessor formStatus: FormErrors = {};
  @observable accessor submitting: boolean | undefined;
  @observable accessor showSuccess: boolean | undefined;

  protected routeStore: RouteStore;
  protected languageStore: LanguageStore;

  constructor(rootStore: RootStore) {
    this.initDefaultValues();
    this.routeStore = rootStore.routeStore;
    this.languageStore = rootStore.languageStore;
  }

  @actionBound
  public initDefaultValues() {
    this.emailRegisterCredentials = { email: '', firstName: '', lastName: '' };
    this.formStatus = {};
    this.submitting = false;
    this.showSuccess = false;
  }

  @computed
  public get getError() {
    return Object.keys(this.formStatus).find((key) => formErrors.includes(key as keyof FormErrors));
  }

  @actionBound
  public async handleEmailRegisterSubmit() {
    this.submitting = true;
    this.showSuccess = false;
    try {
      const response = await API.post(
        `/email/register${this.routeStore.formForwardParamsAnd({ locale: this.languageStore.currentLanguage }, ['locale'])}`,
        this.emailRegisterCredentials,
      );
      this.handleSuccess(response);
    } catch (e: Error | string | any) {
      this.handleError(e);
    }
  }

  @actionBound
  protected handleError(e: AxiosError<UiResponse>) {
    ErrorUtil.pushErrorToTeliaRumAndLog(e);
    this.submitting = false;
    if (e.response?.data.redirectUrl) {
      return this.routeStore.route(e.response.data.redirectUrl);
    }
    this.formStatus.generalError = 'error.general';
  }

  @actionBound
  protected handleSuccess(response: AxiosResponse<FormErrors>) {
    this.formStatus = response.data;
    this.submitting = false;
    if (!this.getError) {
      this.showSuccess = true;
    }
  }

  @actionBound
  public setErrorState(error: keyof FormErrors, label: string) {
    this.formStatus = { [error]: label };
  }

  @actionBound
  public setEmptyFormStatus() {
    this.formStatus = {};
  }
}
